import { useState } from 'react';
import { LockKeyhole } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, Dato, Emblema } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { PasswordField } from '@/components/Field';
import { actualizarSettings } from '@/db/schema';
import { crearVerificador, validarContrasenaNueva, LARGO_MINIMO_CONTRASENA } from '@/db/backup-crypto';
import { mensajeDeError } from '@/db/tx';
import type { Settings } from '@/db/types';
import { describirUltimoRespaldo } from '@/domain/backup-reminder';

type Modo = 'ver' | 'activar' | 'cambiar' | 'desactivar';

/**
 * Cifrado opcional de los respaldos (Req. 6.18 a 6.23). Desactivado al inicio.
 *
 * La contraseña se pide solo al activar o cambiar (la nueva, dos veces), al
 * exportar cifrado y al restaurar un cifrado. Desactivar pide solo una
 * confirmación. La app guarda un verificador, nunca la contraseña.
 */
export function CifradoRespaldos({ settings }: { settings: Settings }) {
  const activo = settings.cifradoRespaldo;
  const [modo, setModo] = useState<Modo>('ver');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [entiendo, setEntiendo] = useState(false);
  const [errorCampo, setErrorCampo] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [mensaje, setMensaje] = useState<{ ok: boolean; texto: string } | null>(null);

  function cerrar() {
    setModo('ver');
    setNueva('');
    setRepetida('');
    setEntiendo(false);
    setErrorCampo(null);
  }

  async function guardarContrasena() {
    const problema = validarContrasenaNueva(nueva, repetida);
    if (problema !== null) {
      setErrorCampo(problema);
      return;
    }
    setTrabajando(true);
    setMensaje(null);
    try {
      const cambiando = modo === 'cambiar';
      await actualizarSettings({ cifradoRespaldo: await crearVerificador(nueva) });
      cerrar();
      setMensaje({
        ok: true,
        texto: cambiando
          ? 'Contraseña cambiada. Los respaldos anteriores se siguen abriendo solo con la contraseña con la que se crearon.'
          : 'Cifrado activado. Desde ahora cada respaldo te pedirá la contraseña.',
      });
    } catch (e) {
      setMensaje({ ok: false, texto: mensajeDeError(e) });
    } finally {
      setTrabajando(false);
    }
  }

  async function desactivar() {
    setTrabajando(true);
    setMensaje(null);
    try {
      await actualizarSettings({ cifradoRespaldo: null });
      cerrar();
      setMensaje({
        ok: true,
        texto:
          'Cifrado desactivado. Los respaldos cifrados que ya tienes siguen necesitando su contraseña.',
      });
    } catch (e) {
      setMensaje({ ok: false, texto: mensajeDeError(e) });
    } finally {
      setTrabajando(false);
    }
  }

  const formulario = (modo === 'activar' || modo === 'cambiar') && (
    <div className="space-y-3">
      {modo === 'activar' && (
        <Banner tono="alerta" titulo="Si olvidas la contraseña, no hay forma de recuperarla">
          Ni la app ni nadie puede abrir un respaldo cifrado sin su contraseña. Guárdala en la app
          Contraseñas del iPhone o en otro lugar seguro.
        </Banner>
      )}
      {modo === 'cambiar' && (
        <Banner tono="info" titulo="Los respaldos anteriores no cambian">
          Cada archivo se abre con la contraseña con la que se creó. Conserva la anterior mientras
          tengas respaldos hechos con ella.
        </Banner>
      )}
      <PasswordField
        etiqueta={modo === 'cambiar' ? 'Contraseña nueva' : 'Contraseña'}
        valor={nueva}
        onCambio={(v) => {
          setNueva(v);
          setErrorCampo(null);
        }}
        autoComplete="new-password"
        ayuda={`Al menos ${LARGO_MINIMO_CONTRASENA} caracteres. Una frase es fácil de recordar.`}
      />
      <PasswordField
        etiqueta="Repite la contraseña"
        valor={repetida}
        onCambio={(v) => {
          setRepetida(v);
          setErrorCampo(null);
        }}
        autoComplete="new-password"
        error={errorCampo}
      />
      {modo === 'activar' && (
        <Casilla marcada={entiendo} onCambio={setEntiendo}>
          Entiendo que si olvido la contraseña no podré recuperar los respaldos cifrados con ella.
        </Casilla>
      )}
      <Button
        disabled={
          trabajando || nueva === '' || repetida === '' || (modo === 'activar' && !entiendo)
        }
        onClick={() => void guardarContrasena()}
        {...(modo === 'activar' ? { icono: LockKeyhole } : {})}
      >
        {trabajando
          ? 'Guardando…'
          : modo === 'activar'
            ? 'Activar cifrado'
            : 'Guardar la contraseña nueva'}
      </Button>
      <Button variante="secundario" disabled={trabajando} onClick={cerrar}>
        Cancelar
      </Button>
    </div>
  );

  return (
    <Card className="space-y-4">
      <div>
        <Emblema icono={LockKeyhole} />
        <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">
          Cifrado opcional
        </h2>
        <p className="text-[13px] leading-relaxed text-texto-suave">
          DuoFit no guarda tu contraseña. Si la olvidas, no podrás abrir los respaldos cifrados.
        </p>
      </div>

      <dl>
        <Dato
          etiqueta="Estado"
          valor={activo === null ? 'Desactivado' : 'Activado'}
          {...(activo !== null
            ? { detalle: `Desde el ${describirUltimoRespaldo(activo.activadoEn)}` }
            : {})}
        />
      </dl>

      {modo === 'ver' && activo === null && (
        <Button variante="secundario" onClick={() => setModo('activar')} icono={LockKeyhole}>
          Activar cifrado
        </Button>
      )}
      {modo === 'ver' && activo !== null && (
        <div className="space-y-2">
          <Button variante="secundario" onClick={() => setModo('cambiar')}>
            Cambiar la contraseña
          </Button>
          <Button variante="fantasma" onClick={() => setModo('desactivar')}>
            Desactivar cifrado
          </Button>
        </div>
      )}

      {formulario}

      {modo === 'desactivar' && (
        <Banner tono="aviso" titulo="¿Desactivar el cifrado?">
          <p>
            Los respaldos nuevos se guardarán sin contraseña. Los cifrados que ya existen siguen
            necesitando la suya.
          </p>
          <div className="mt-3 space-y-2">
            <Button variante="peligro" disabled={trabajando} onClick={() => void desactivar()}>
              {trabajando ? 'Desactivando…' : 'Sí, desactivar'}
            </Button>
            <Button variante="secundario" disabled={trabajando} onClick={cerrar}>
              Cancelar
            </Button>
          </div>
        </Banner>
      )}

      {mensaje !== null && (
        <Banner
          tono={mensaje.ok ? 'exito' : 'alerta'}
          titulo={mensaje.ok ? 'Listo' : 'No se pudo guardar'}
        >
          {mensaje.texto}
        </Banner>
      )}
    </Card>
  );
}
